import { CandidateDeclarationSource } from "constants/candidateDeclarations";
import { get } from "lodash";

function createCandidateDeclarationGetter(field: string) {
  return (candidateDeclaration: CandidateDeclaration | null) =>
    get(candidateDeclaration, field);
}

export const commonCandidateDeclarationFields = {
  NVD: createCandidateDeclarationGetter("serialNumber"),
  vendor_property_id: createCandidateDeclarationGetter("originPropertyId"),
  "declaration.VERSION": createCandidateDeclarationGetter("nvdVersion"),
};

function yesNoNotAnsweredTransformer(value) {
  if (value === "Yes") {
    return true;
  }
  if (value === "No") {
    return false;
  }
  if (value !== null && value !== undefined) {
    return undefined;
  }

  return value;
}

const MIN_RADIO_FIELD_CONFIDENCE: number = 0.5;
const MIN_RADIO_FIELD_ALIGNMENT_SCORE: number = 0.2;

function getRadioControlAnswer(
  fieldName: string,
  transformer = yesNoNotAnsweredTransformer,
) {
  return (candidateDeclaration: CandidateDeclaration | null) => {
    const value = get(candidateDeclaration, `declarationContent.${fieldName}`);

    if (!value) {
      return undefined;
    }

    if (
      value.metadata?.field_alignment_score < MIN_RADIO_FIELD_ALIGNMENT_SCORE
    ) {
      // anything below this alignment score is basically garbage
      return transformer(undefined);
    }

    // Status takes precedence over confidence — check it first regardless of confidence value
    const failureReason = value.metadata?.status;
    if (
      ["neither_marked", "alignment_failed", "ambiguous"].includes(
        failureReason,
      )
    ) {
      return transformer(undefined);
    }

    if (value.confidence < MIN_RADIO_FIELD_CONFIDENCE) {
      return undefined;
    }
    return transformer(value.text);
  };
}

function getSheepOwnedSinceBirth(value) {
  return (
    {
      "Less than 2 months": "A",
      "2-6 months": "B",
      "6-12 months": "C",
      "More than 12 months": "D",
    }[value] || undefined
  );
}

export const scannedNvdSuggestedValueMap = {
  ...commonCandidateDeclarationFields,
  "declaration.qa_accredited": getRadioControlAnswer("qa_program"),
  "declaration.treated_for_scabby_mouth": getRadioControlAnswer("scabby_mouth"),
  "declaration.treated_with_hgp": getRadioControlAnswer("hgp_treated"),
  "declaration.fed_feed_containing_animal_fats":
    getRadioControlAnswer("animal_fats"),
  "declaration.owned_since_birth": getRadioControlAnswer("owned_since_birth"),
  "declaration.owned_since_birth_duration": getRadioControlAnswer(
    "ownership_duration",
    getSheepOwnedSinceBirth,
  ),
  "declaration.fed_byproduct_stockfeeds": getRadioControlAnswer(
    "byproduct_stockfeeds",
  ),
  "declaration.chemical_residue_restrictions":
    getRadioControlAnswer("erp_restrictions"),
  "declaration.veterinary_treatment": getRadioControlAnswer("whp_esi"),
  "declaration.consumed_material_in_withholding_period": getRadioControlAnswer(
    "consumed_whp_material",
  ),
  "declaration.spray_drift_risk": getRadioControlAnswer("spray_risk"),
};

export function getCandidateDeclarationAnswers(
  candidateDeclaration: CandidateDeclaration | null,
): { questionId: string; value: string }[] {
  return get(candidateDeclaration, "declarationContent.answers") || [];
}

function getEnvdAnswer(
  questionId: number,
  transformer = yesNoNotAnsweredTransformer,
) {
  return (candidateDeclaration: CandidateDeclaration | null) => {
    const answer = getCandidateDeclarationAnswers(candidateDeclaration).find(
      a =>
        // Question Ids are stored as strings, compare coercively
        a.questionId === String(questionId),
    );

    if (!answer) {
      return "";
    }

    return transformer(answer.value);
  };
}

function getOwnedSinceBirthDuration(
  candidateDeclaration: CandidateDeclaration | null,
) {
  const answers = getCandidateDeclarationAnswers(candidateDeclaration);
  const sheepAnswer = answers.find(a => a.questionId === String(19));
  if (sheepAnswer) {
    return (
      {
        "Less than 2 months": "A",
        "2-6 months": "B",
        "6-12 months": "C",
        "more than 12 months": "D",
      }[sheepAnswer.value] || null
    );
  }
  const cattleAnswer = answers.find(a => a.questionId === String(20));
  if (cattleAnswer) {
    return (
      {
        LT2Months: "A",
        "2to6Months": "B",
        "6to12Months": "C",
        GT12Months: "D",
      }[cattleAnswer.value] || null
    );
  }
  return null;
}

export const eNvdCandidateDecMap = {
  ...commonCandidateDeclarationFields,

  quantity_NVD: createCandidateDeclarationGetter("declarationContent.heads"),

  // Sheep Q1
  "declaration.qa_accredited": getEnvdAnswer(30),

  // Cattle Q1
  "declaration.treated_with_hgp": getEnvdAnswer(88),

  // Sheep Q2
  "declaration.treated_for_scabby_mouth": getEnvdAnswer(96), // ERR this is the same as vet treatment - we need to double check this!!!

  // Sheep Q3 / Cattle Q3
  "declaration.owned_since_birth": getEnvdAnswer(17),

  "declaration.owned_since_birth_duration": getOwnedSinceBirthDuration,

  // Sheep Q4 / Cattle Q6
  "declaration.veterinary_treatment": getEnvdAnswer(96), // ERR this is the same as scabby mouth - we need to double check this!!!

  // Cattle 04
  "declaration.fed_byproduct_stockfeeds": getEnvdAnswer(59),

  // Sheep Q5 / Cattle Q7
  "declaration.consumed_material_in_withholding_period": getEnvdAnswer(70),

  // Cattle Q5
  "declaration.chemical_residue_restrictions": getEnvdAnswer(56),

  // Sheep Q6 / Cattle Q2
  "declaration.fed_feed_containing_animal_fats": getEnvdAnswer(58),

  // Cattle Q8
  "declaration.spray_drift_risk": getEnvdAnswer(102),
};

export type CandidateDeclarationFieldGetter = (
  candidateDeclaration: CandidateDeclaration | null,
) => unknown;

export function extractOcrDeclaration(
  candidateDeclaration: CandidateDeclaration | null,
  hasPermission: boolean,
): Record<string, unknown> {
  if (
    !hasPermission ||
    !candidateDeclaration ||
    candidateDeclaration.source !==
      CandidateDeclarationSource.CONSIGNMENT_ATTACHMENT_OCR
  ) {
    return {};
  }
  return Object.fromEntries(
    Object.entries(scannedNvdSuggestedValueMap)
      .filter(
        ([field]) =>
          field.startsWith("declaration.") && field !== "declaration.VERSION",
      )
      .flatMap(([field, getter]) => {
        const declarationField = field.replace("declaration.", "");
        const value = getter(candidateDeclaration);
        return value !== undefined ? [[declarationField, value]] : [];
      }),
  );
}

export function applyOcrDeclaration(
  suggestedDeclaration: Record<string, unknown>,
  existingDeclaration: Record<string, unknown>,
): Record<string, unknown> {
  return { ...suggestedDeclaration, ...existingDeclaration };
}
